import React from 'react';
import { toast } from 'sonner';
import { Loader2, PencilLine, Send } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { adminApi } from '../../../lib/adminAuth';
import { Field } from './ContractDetailsForm';
import { addMonthsEnd, errMsg, fmtDate, inr } from './docUtils';

const nextDay = (ymd) => { if (!ymd) return ''; const d = new Date(`${ymd}T00:00:00`); d.setDate(d.getDate() + 1); return d.toLocaleDateString('en-CA'); };

export default function RenewDialog({ contract, onClose, onRenewed }) {
  const [term, setTerm] = React.useState(12);
  const [start, setStart] = React.useState('');
  const [inc, setInc] = React.useState('none');
  const [val, setVal] = React.useState('');
  const [busy, setBusy] = React.useState('');
  React.useEffect(() => {
    if (!contract) return;
    setTerm(contract.term_months || 12); setStart(nextDay(contract.end_date) || ''); setInc('none'); setVal('');
  }, [contract]);
  if (!contract) return null;
  const fee = Number(contract.fee || 0);
  const v = Number(val || 0);
  const newFee = inc === 'amount' ? fee + v : inc === 'percent' ? Math.round(fee * (1 + v / 100) * 100) / 100 : fee;
  const end = addMonthsEnd(start, term);

  const renew = async (mode) => {
    setBusy(mode);
    try {
      const r = await adminApi.post(`/api/admin/contracts/${contract._id}/renew`, { term_months: Number(term), start_date: start || null, increase_type: inc, increase_value: v });
      toast.success(`${r.data.contract_number} created as the renewal`);
      onRenewed(r.data._id, mode);
    } catch (e) { toast.error(errMsg(e, 'Could not renew')); }
    finally { setBusy(''); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="renew-dialog">
        <DialogHeader><DialogTitle>Renew {contract.contract_number}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">{contract.brand_name || contract.client_name} · current term ends {fmtDate(contract.end_date)}. The renewal is a new contract the client signs online; this one stays as history.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="New term (months)"><Input type="number" min="1" max="120" value={term} onChange={(e) => setTerm(e.target.value)} data-testid="renew-term" /></Field>
          <Field label="Starts on" hint={end ? `Ends ${fmtDate(end)}` : ''}><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="renew-start" /></Field>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-600 mb-1.5">Fee increase?</p>
          <div className="grid grid-cols-3 gap-1 p-1 rounded-xl bg-slate-100">
            {[['none', 'Skip'], ['amount', 'By amount'], ['percent', 'By %']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setInc(k)} data-testid={`renew-increase-${k}`}
                className={`py-1.5 rounded-lg text-xs font-semibold transition-colors ${inc === k ? 'bg-gray-900 text-white shadow-sm' : 'text-slate-600 hover:bg-white/70'}`}>{l}</button>
            ))}
          </div>
          {inc !== 'none' && (
            <Input className="mt-2" type="number" min="0" value={val} onChange={(e) => setVal(e.target.value)}
              placeholder={inc === 'amount' ? 'Increase in ₹ per month' : 'Increase in %'} data-testid="renew-increase-value" />
          )}
          <p className="mt-2 text-sm" data-testid="renew-fee-preview">Monthly fee: <span className="text-slate-400 line-through mr-1">{newFee !== fee ? inr(fee) : ''}</span><b>{inr(newFee)}</b> <span className="text-xs text-slate-400">excl. GST</span></p>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" disabled={!!busy || !term} onClick={() => renew('edit')} data-testid="renew-with-edits">
            {busy === 'edit' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <PencilLine className="w-4 h-4 mr-1.5" />} Renew with edits
          </Button>
          <Button disabled={!!busy || !term} onClick={() => renew('send')} className="bg-gray-900 hover:bg-gray-800" data-testid="renew-as-is">
            {busy === 'send' ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />} Renew as is &amp; send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
